import { AxiosResponse } from "axios";
import useSWR, { KeyedMutator } from "swr";
import { DisplayGroup } from "@/models/displayGroup";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import DisplayGroupEndpoints from "@/services/displayGroup/displayGroup.endpoint";
import { DisplayGroupSchema } from "./displayGroup.api.types";

export class DisplayGroupService {
  static getDisplayGroupsByWorkspaceId(workspaceId: string): {
    data: DisplayGroup[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<DisplayGroupSchema[], undefined>>;
  } {
    const url = DisplayGroupEndpoints.GET_DISPLAY_GROUPS_BY_WORKSPACE(workspaceId);

    const fetcher = () =>
      ApiService.fetchData<undefined, DisplayGroupSchema[]>({
        url,
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      workspaceId ? url : null, // ワークスペースIDがある場合のみ、fetchする
      fetcher,
      defaultSWROption,
    );

    return {
      data: data?.data.map(buildDisplayGroup),
      error,
      isLoading,
      mutate,
    };
  }

  static async create(name: string, paths: string[], workspaceId: string) {
    const data = {
      name,
      categoryPaths: paths,
      workspaceId,
    };
    await ApiService.fetchData<{ name: string; categoryPaths: string[]; workspaceId: string; }, void>(
      {
        url: DisplayGroupEndpoints.CREATE_DISPLAY_GROUP,
        method: "POST",
        data,
      },
    );
  }

  static async update(displayGroup: DisplayGroup) {
    const data = {
      name: displayGroup.name,
      categoryPaths: displayGroup.categoryPaths,
    };
    await ApiService.fetchData<{ name: string; categoryPaths: string[]; }, void>(
      {
        url: DisplayGroupEndpoints.UPDATE_DISPLAY_GROUP(displayGroup.id),
        method: "PUT",
        data,
      },
    );
  }

  static async delete(id: string) {
    await ApiService.fetchData<void, void>({
      url: DisplayGroupEndpoints.DELETE_DISPLAY_GROUP(id),
      method: "DELETE",
    });
  }
}

const buildDisplayGroup = (displayGroup: DisplayGroupSchema) =>
  new DisplayGroup(
    displayGroup.id,
    displayGroup.name,
    displayGroup.order,
    displayGroup.categoryPaths,
  );
